import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { RowContainer } from "../../../components/containers/styledComponents";
import { DataPoint } from "./util";
import { StyledTable } from "../../../components/tables/styledComponents";
import { CustomTableCell } from "../../../components/tables/CustomTableCell";
import {
  CellType,
  ColumnType as CustomColumnType,
  TableCellType,
} from "../../../models/tableModels";
import { ColumnType } from "antd/es/table";
import { usePost } from "../../../hooks/usePost";
import {
  CompanyLogoProps,
  CompanyLogoRoute,
} from "../../../components/tables/CompanyLogo";
import { TopicMapping } from "../../../models/stewardshipModels";
import { AvatarMatrixProps } from "../../../components/visuals/Avatar";
import { ENGAGEMENT_META_THEME_IMAGES } from "../../CompanyEngagements/constants";
import { formatPct, formatReadable } from "../../../utils/formattingUtils";
import { MESSAGES } from "../../../messages";
import clusterIcon from "../../../assets/svg/cluster_icon_dark.svg";
import { TABLE_BASE_HEIGHT } from "../../../styles";
import { useToast } from "../../../store/ToastProvider";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
} from "../../../components/common/Modal";

const TableContainer = styled.div`
  width: 100%;
  height: ${TABLE_BASE_HEIGHT + 100}px;
`;

interface EngagementMaterialityClusterModalProps {
  show: boolean;
  handleClose: () => void;
  rawClusterData: DataPoint[];
}

interface MappingBadges {
  badges: AvatarMatrixProps;
}

type CompanyEngagementMateriality = {
  coverage: number;
  name: CompanyLogoProps;
} & {
  topicMappings: TopicMapping[];
} & MappingBadges;

const PAGE_SIZE = 20;

const aggregateTopicMappings = (
  topicMappings: TopicMapping[]
): Record<string, TopicMapping[]> => {
  const grouped = topicMappings.reduce<Record<string, TopicMapping[]>>(
    (acc, mapping) => {
      if (!acc[mapping.compass_cid]) {
        acc[mapping.compass_cid] = [];
      }
      acc[mapping.compass_cid].push({
        compass_cid: mapping.compass_cid,
        meta_theme: mapping.meta_theme,
        theme: mapping.theme,
        sub_theme: mapping.sub_theme,
        is_mapped: mapping.is_mapped,
        sub_sector: mapping.sub_sector,
      });
      return acc;
    },
    {}
  );

  Object.keys(grouped).forEach((cid) => {
    grouped[cid] = Array.from(
      new Map(grouped[cid].map((m) => [m.sub_theme, m])).values()
    );
  });

  return grouped;
};

const getBadges = (mappings: TopicMapping[]): AvatarMatrixProps => {
  enum LogoKey {
    logo = "logo",
    disabledLogo = "disabledLogo",
  }
  const avatars = mappings.map((mapping) => {
    const logoIndexingKey: LogoKey = mapping.is_mapped
      ? LogoKey.logo
      : LogoKey.disabledLogo;

    return {
      username: mapping.sub_theme,
      displayText: formatReadable(mapping?.sub_theme || ""),
      image:
        ENGAGEMENT_META_THEME_IMAGES[mapping.meta_theme]?.[logoIndexingKey],
      tooltip: true,
      ...mapping,
    };
  });

  const grouped: Record<string, typeof avatars> = {};
  avatars.forEach((avatar) => {
    if (!grouped[avatar.meta_theme]) {
      grouped[avatar.meta_theme] = [];
    }
    grouped[avatar.meta_theme].push(avatar);
  });

  const stacks = Object.keys(grouped)
    .sort()
    .map((metaTheme) =>
      grouped[metaTheme].sort((a, b): number => {
        if (a.is_mapped !== b.is_mapped) {
          return a.is_mapped ? -1 : 1;
        }
        return a.sub_theme.localeCompare(b.sub_theme);
      })
    );

  return { stacks };
};

const mergeDataPointsWithTopics = (
  dataPoints: DataPoint[],
  topicMappings: TopicMapping[]
): CompanyEngagementMateriality[] => {
  const aggregated: Record<string, TopicMapping[]> =
    aggregateTopicMappings(topicMappings);

  return dataPoints.map((dp) => {
    const mappings = aggregated[dp.id] || [];
    const totalTopics = mappings.length;
    const coveredTopics = mappings.filter((m) => m.is_mapped).length;
    const calculatedCoverage =
      totalTopics > 0 ? coveredTopics / totalTopics : 0;

    return {
      name: {
        name: dp.name,
        logoUrl: dp.image,
        companyId: dp.id,
        route: CompanyLogoRoute.COMPANY_ENGAGEMENTS,
      } as CompanyLogoProps,
      weight: formatPct(dp.weight as number),
      coverage: calculatedCoverage,
      badges: getBadges(mappings),
      topicMappings: mappings,
      sub_sector: mappings[0]?.sub_sector || MESSAGES.NOT_AVAILABLE,
    } as CompanyEngagementMateriality;
  });
};

export const STEWARDSHIP_SCREENER_BASE_COLUMNS: ColumnType<CompanyEngagementMateriality>[] =
  [
    {
      title: "Company Name",
      key: "name",
      dataIndex: "name",
      fixed: "left",
    },
    {
      title: "Relative Weight",
      key: "weight",
      dataIndex: "weight",
    },
    {
      title: "Sub-sector",
      key: "sub_sector",
      dataIndex: "sub_sector",
    },

    {
      title: "Covered Issues",
      key: "coverage",
      dataIndex: "coverage",
    },
    {
      title: "Badges",
      key: "badges",
      dataIndex: "badges",
      fixed: "right",
    },
  ];

export const STEWARDSHIP_SCREENER_COLUMN_TYPES: CustomColumnType[] = [
  { type: TableCellType.VOTING_ALIGNMENT_BAR, columnKey: "coverage" },
  { type: TableCellType.COMPANY_LOGO, columnKey: "name" },
  { type: TableCellType.AVATAR_MATRIX, columnKey: "badges" },
];

export const EngagementMaterialityClusterModal: React.FC<
  EngagementMaterialityClusterModalProps
> = ({ show, handleClose, rawClusterData }) => {
  const [pagination, setPagination] = useState<number>(1);
  const { addToast } = useToast();
  const {
    error,
    sendData: fetchCompaniesEngagementTopicMapping,
    isLoading,
    data: engagementTopicMappingData,
  } = usePost<TopicMapping[], { compass_cids: string[] }>();
  useEffect(() => {
    fetchCompaniesEngagementTopicMapping(
      "GET_COMPANIES_ENGAGEMENT_TOPIC_MAPPING",
      {
        compass_cids: rawClusterData
          .map((d) => d.id)
          .slice((pagination - 1) * PAGE_SIZE, pagination * PAGE_SIZE),
      }
    );
  }, [rawClusterData, pagination]);

  useEffect(() => {
    !show && setPagination(1);
  }, [show]);

  useEffect(() => {
    if (error) {
      addToast("Error loading companies engagement topic mapping", "error");
      handleClose();
    }
  }, [error]);

  const companyEngagementMaterialitySource = useMemo(() => {
    if (!engagementTopicMappingData?.length) return [];
    const paginationAdjustedClusterData = rawClusterData.slice(
      (pagination - 1) * PAGE_SIZE,
      pagination * PAGE_SIZE
    );
    return mergeDataPointsWithTopics(
      paginationAdjustedClusterData,
      engagementTopicMappingData
    );
  }, [engagementTopicMappingData]);

  const footerContent = (
    <ModalFooter onClose={handleClose} hideSubmit showDivider={false} />
  );

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      size="xl"
      title={MESSAGES.STEWARDSHIP.MATERIALITY.SELECTED_CLUSTER_TITLE}
      footer={footerContent}
      titleIcon={clusterIcon}
      modalType="lg"
    >
      <ModalContent>
        <RowContainer jc="space-between" ai="center" mt={4} mb={4}>
          <TableContainer>
            <StyledTable
              isLoading={isLoading}
              dataSource={
                isLoading
                  ? Array.from(
                      { length: 10 },
                      (_, i) => rawClusterData[i % rawClusterData.length]
                    )
                  : companyEngagementMaterialitySource
              }
              hasEveryCellBorderBottom={true}
              scroll={{ y: TABLE_BASE_HEIGHT, x: "max-content" }}
              pagination={{
                pageSize: PAGE_SIZE,
                total: rawClusterData.length,
                showSizeChanger: false,
                current: pagination,
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} records`,
              }}
              onChange={(pagination): void =>
                setPagination(pagination.current || 1)
              }
            >
              {STEWARDSHIP_SCREENER_BASE_COLUMNS.map((column) => (
                <StyledTable.Column
                  key={column.key}
                  dataIndex={column.dataIndex}
                  title={column.title}
                  fixed={column.fixed}
                  render={(value: CellType): JSX.Element => (
                    <CustomTableCell
                      value={value}
                      type={
                        isLoading
                          ? TableCellType.LOADING
                          : STEWARDSHIP_SCREENER_COLUMN_TYPES.find(
                              (col) => col.columnKey === column.dataIndex
                            )?.type || TableCellType.TEXT
                      }
                    />
                  )}
                />
              ))}
            </StyledTable>
          </TableContainer>
        </RowContainer>
      </ModalContent>
    </CompassModal>
  );
};
